<template>
    <form
        ref="form"
        @submit.prevent="updateListing"
        :action="route"
        autocomplete="off"
        class="p-4 rounded bg-white shadow-lg border-2 border-green-500"
    >
        <input type="hidden" name="_method" value="put">

        <div class="space-y-4">

            <heading-2>{{ listing.title ? 'Update' : 'Create' }} Your Listing</heading-2>
            <div class="space-y-8">
                <div class="space-y-4">
                    <text-input
                        v-model="form.title"
                        :error="form.errors.title"
                        label="Title"
                        name="title"
                        placeholder="Bob's Firewood"
                    />
                    <text-input
                        v-model="form.website"
                        :error="form.errors.website"
                        label="Website"
                        name="website"
                        placeholder="https://bobs-firewood.com"
                    />
                    <text-input
                        v-model="form.email"
                        :error="form.errors.email"
                        label="Email"
                        name="email"
                        help="This is the email that will be shown on your listing, and can be different than your user."
                        placeholder="help@bobs-firewood.com"
                    />
                    <text-input
                        v-model="form.phone_number"
                        :error="form.errors.phone_number"
                        label="Phone Number"
                        name="phone_number"
                        placeholder="555-867-5309"
                    />

                    <div class="space-y-2">
                        <autocomplete-address
                            placeholder="1600 Pennsylvania Avenue NW"
                            label="Address"
                            v-model="form.address.street_1"
                            v-model:address="form.address"
                            :error="addressError"
                        />

                        <input v-for="(value, key) in addressInputs" :key="key" type="hidden" :name="`address[${key}]`" :value="value" class="hidden">

                        <text-input
                            v-model="form.address.street_2"
                            :error="form.errors['address.street_2']"
                            placeholder="Suite B"
                        />

                        <text-input
                            v-model="form.address.city"
                            :error="form.errors['address.city']"
                            placeholder="Washington"
                        />

                        <text-input
                            v-model="form.address.state"
                            :error="form.errors['address.state']"
                            placeholder="DC"
                        />

                        <text-input
                            v-model="form.address.postal_code"
                            :error="form.errors['address.postal_code']"
                            placeholder="20500"
                        />

                        <small class="form-help">This address is used to determine delivery distance and will only be shown on the site if you offer pickup.</small>

                    </div>

                    <file-input
                        v-model="form.photo"
                        :error="form.errors.photo"
                        label="Photo"
                        name="photo"
                    />

                    <div v-if="photo">
                        <img :src="photo" :alt="listing.title">
                    </div>
                </div>
                <div class="space-y-4">
                    <div class="form-label">Offerings:</div>
                    <checkbox-input
                        v-model:checked="form.offerings"
                        value="delivery"
                        label="Delivery"
                        name="offerings[]"
                    />
                    <checkbox-input
                        v-model:checked="form.offerings"
                        value="pickup"
                        label="Pickup"
                        name="offerings[]"
                    />
                    <small v-if="form.errors.offerings" class="form-error">{{ form.errors.offerings }}</small>
                </div>
                <div
                    v-if="form.offerings.includes('delivery')"
                    class="space-y-4"
                >
                    <div class="form-label">DELIVERY OPTIONS:</div>
                    <text-input
                        v-model="form.delivery_distance"
                        :error="form.errors.delivery_distance"
                        label="Delivery Distance (miles)"
                        name="delivery_distance"
                        placeholder="10"
                        help="The distance you are willing to travel from the address above to deliver firewood."
                    />
                    <text-input
                        v-model="form.delivery_cost"
                        :error="form.errors.delivery_cost"
                        label="Delivery Cost"
                        name="delivery_cost"
                        placeholder="10"
                    />
                </div>
                <div
                    v-if="form.offerings.includes('pickup')"
                    class="space-y-4"
                >
                    <div class="form-label">PICK UP OPTIONS:</div>
                    <div class="form-label">Hours:</div>
                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Monday"
                            name="open_on[]"
                            value="monday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('monday')"
                            day="monday"
                            v-model:opens-at="form.opens_monday_at"
                            v-model:closes-at="form.closes_monday_at"
                            :opens-at-error="form.errors.opens_monday_at"
                            :closes-at-error="form.errors.closes_monday_at"
                        />
                    </div>

                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Tuesday"
                            name="open_on[]"
                            value="tuesday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('tuesday')"
                            day="tuesday"
                            v-model:opens-at="form.opens_tuesday_at"
                            v-model:closes-at="form.closes_tuesday_at"
                            :opens-at-error="form.errors.opens_tuesday_at"
                            :closes-at-error="form.errors.closes_tuesday_at"
                        />
                    </div>

                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Wednesday"
                            name="open_on[]"
                            value="wednesday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('wednesday')"
                            day="wednesday"
                            v-model:opens-at="form.opens_wednesday_at"
                            v-model:closes-at="form.closes_wednesday_at"
                            :opens-at-error="form.errors.opens_wednesday_at"
                            :closes-at-error="form.errors.closes_wednesday_at"
                        />
                    </div>

                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Thursday"
                            name="open_on[]"
                            value="thursday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('thursday')"
                            day="thursday"
                            v-model:opens-at="form.opens_thursday_at"
                            v-model:closes-at="form.closes_thursday_at"
                            :opens-at-error="form.errors.opens_thursday_at"
                            :closes-at-error="form.errors.closes_thursday_at"
                        />
                    </div>

                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Friday"
                            name="open_on[]"
                            value="friday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('friday')"
                            day="friday"
                            v-model:opens-at="form.opens_friday_at"
                            v-model:closes-at="form.closes_friday_at"
                            :opens-at-error="form.errors.opens_friday_at"
                            :closes-at-error="form.errors.closes_friday_at"
                        />
                    </div>

                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Saturday"
                            name="open_on[]"
                            value="saturday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('saturday')"
                            day="saturday"
                            v-model:opens-at="form.opens_saturday_at"
                            v-model:closes-at="form.closes_saturday_at"
                            :opens-at-error="form.errors.opens_saturday_at"
                            :closes-at-error="form.errors.closes_saturday_at"
                        />
                    </div>

                    <div class="space-y-4">
                        <checkbox-input
                            v-model:checked="form.open_on"
                            label="Open Sunday"
                            name="open_on[]"
                            value="sunday"
                        />

                        <open-close-times-input
                            v-if="form.open_on.includes('sunday')"
                            day="sunday"
                            v-model:opens-at="form.opens_sunday_at"
                            v-model:closes-at="form.closes_sunday_at"
                            :opens-at-error="form.errors.opens_sunday_at"
                            :closes-at-error="form.errors.closes_sunday_at"
                        />
                    </div>

                    <small v-if="form.errors.open_on" class="form-error">{{ form.errors.open_on }}</small>
                </div>

                <div v-if="form.offerings.length > 0" class="space-y-4">
                    <Heading2>Products</Heading2>


                    <div class="space-y-8">
                        <div
                            v-for="(product, index) in form.products"
                            :key="index"
                        >
                            <input v-if="product.id" type="hidden" :name="'products['+ index +'][id]'" :value="product.id">
                            <div class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                                <TextInput
                                    label="Name"
                                    placeholder="Face Cord"
                                    v-model="product.name"
                                    :name="'products['+ index +'][name]'"
                                    :error="form.errors[`products.${index}.name`]"
                                />
                                <TextInput
                                    label="Price"
                                    placeholder="100"
                                    v-model="product.price"
                                    :name="'products['+ index +'][price]'"
                                    :error="form.errors[`products.${index}.price`]"
                                />

                                <button
                                    v-if="form.products.length > 1"
                                    @click.prevent="removeProduct(index)"
                                    type="button"
                                    class="block btn btn-gray sm:col-span-2"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>

                        <button
                            @click.prevent="addProduct"
                            type="button"
                            class="block w-full btn btn-gray"
                        >
                            Add Another Product
                        </button>
                    </div>
                </div>
            </div>
            <div class="flex justify-end">
                <loading-button
                    :loading="form.processing"
                    type="submit"
                    class="btn btn-green"
                >
                    Update
                </loading-button>
            </div>
        </div>
    </form>

    <success-modal
        v-model="notification.success"
    >
        <div class="space-y-4">
            <h2 class="heading">Hooray!</h2>
            <p class="text-lg text-gray-800">Your listing was successfully updated.</p>
        </div>
    </success-modal>

    <error-modal
        v-model="notification.error"
    >
        <div class="space-y-4">
            <h2 class="heading">Bummer...</h2>
            <p class="text-lg text-gray-800">Your listing has <span class="font-semibold">{{ size(form.errors) }} {{ pluralize('error', size(form.errors)) }}</span>.</p>
            <p class="text-lg text-gray-800">Please correct your {{ pluralize('mistake', size(form.errors)) }} and try again.</p>
        </div>
    </error-modal>
</template>

<script>
import LoadingButton from './shared/LoadingButton'
import TextInput from './shared/TextInput'
import CheckboxInput from './shared/CheckboxInput'
import SelectInput from './shared/SelectInput'
import AutocompleteAddress from './shared/AutocompleteAddress'
import OpenCloseTimesInput from './shared/OpenCloseTimesInput'
import FileInput from './shared/FileInput'
import Heading2 from './shared/Heading2'
import Icon from './shared/Icon'
import SuccessModal from '@/components/shared/SuccessModal'
import ErrorModal from '@/components/shared/ErrorModal'
import Form from '@/functions/Form'
import { omitBy, pickBy, merge, size } from 'lodash'
import pluralize from 'pluralize'

export default {
    props: {
        route: {
            type: String,
            required: true,
        },
        listing: {
            type: [Object],
            default: () => ({})
        },
    },
    data() {
        return {
            form: Form({
                title: this.listing?.title,
                website: this.listing?.website,
                email: this.listing?.email,
                phone_number: this.listing?.phone_number,
                address: {
                    street_1: this.listing?.address.street_1,
                    street_2: this.listing?.address.street_2,
                    city: this.listing?.address.city,
                    state: this.listing?.address.state,
                    postal_code: this.listing?.address.postal_code,
                    country: this.listing?.address.country,
                    latitude: this.listing?.address.latitude,
                    longitude: this.listing?.address.longitude,
                },
                photo: null,
                offerings: this.listing?.offerings ?? [],
                delivery_distance: this.listing?.delivery_distance,
                delivery_cost: this.listing?.delivery_cost,
                open_on: this.listing?.open_on ?? [],
                opens_monday_at: this.listing?.opens_monday_at ?? null,
                closes_monday_at: this.listing?.closes_monday_at ?? null,
                opens_tuesday_at: this.listing?.opens_tuesday_at ?? null,
                closes_tuesday_at: this.listing?.closes_tuesday_at ?? null,
                opens_wednesday_at: this.listing?.opens_wednesday_at ?? null,
                closes_wednesday_at: this.listing?.closes_wednesday_at ?? null,
                opens_thursday_at: this.listing?.opens_thursday_at ?? null,
                closes_thursday_at: this.listing?.closes_thursday_at ?? null,
                opens_friday_at: this.listing?.opens_friday_at ?? null,
                closes_friday_at: this.listing?.closes_friday_at ?? null,
                opens_saturday_at: this.listing?.opens_saturday_at ?? null,
                closes_saturday_at: this.listing?.closes_saturday_at ?? null,
                opens_sunday_at: this.listing?.opens_sunday_at ?? null,
                closes_sunday_at: this.listing?.closes_sunday_at ?? null,
                products: this.listing?.products.length > 0 ? this.listing.products : [{}],
            }),
            photo: this.listing?.photo ?? null,
            notification: {
                success: false,
                error: false,
            }
        }
    },
    computed: {
        addressError() {
            return this.form.errors.address
                || this.form.errors['address.street_1']
                || this.form.errors['address.city']
                || this.form.errors['address.state']
                || this.form.errors['address.postal_code']
        },
        addressInputs() {
            return pickBy(this.form.address)
        },
    },
    watch: {
        'form.wasSuccessful': function(value) {
            this.notification.success = value
        },
        'form.hasErrors': function(value) {
            this.notification.error = value
        }
    },
    methods: {
        updateListing() {
            this
                .form
                .transform(() => new FormData(this.$refs.form))
                .post(this.route, {
                    onSuccess: res => {
                        merge(this.form, res, { photo: null})
                        this.photo = res.photo
                        this.$root.$refs.scroll.scrollTo(0, 0)
                    }
                })
        },
        addProduct() {
            this.form.products.push({})
        },
        removeProduct(index) {
            this.form.products.splice(index, 1)
            this.form.errors = omitBy(this.form.errors, (value, key) => key.startsWith('products'))
        },
        pluralize,
        size,
    },
    components: {
        LoadingButton,
        TextInput,
        CheckboxInput,
        SelectInput,
        FileInput,
        OpenCloseTimesInput,
        Heading2,
        Icon,
        AutocompleteAddress,
        SuccessModal,
        ErrorModal,
    }
}
</script>
